// Developer dashboard — the projects a tester assigned to this developer.
// Read-only by design: a developer never creates, crawls, generates or runs.
// They open a project to read its report and mark what they've fixed.
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FolderKanban, Globe, Search, Filter, ArrowRight, Bug,
  FileText, Boxes, CheckCircle2, WifiOff, RotateCw, Loader2,
} from "lucide-react";
import Aurora from "@/components/common/Aurora";
import EmptyState from "@/components/shared/EmptyState";
import { cn } from "@/lib/cn";
import { listDevProjects } from "@/api/developer";
import { formatDate } from "@/lib/dates";

const fmtWhen = (v) => {
  if (!v) return "—";
  return formatDate(v);
};

function CardSkeleton() {
  return (
    <div className="card p-5">
      <div className="flex items-center gap-2.5">
        <div className="h-10 w-10 animate-pulse rounded-xl bg-slate-200 dark:bg-white/10" />
        <div className="h-4 w-32 animate-pulse rounded bg-slate-200 dark:bg-white/10" />
      </div>
      <div className="mt-3 h-3 w-44 animate-pulse rounded bg-slate-200 dark:bg-white/10" />
      <div className="mt-4 grid grid-cols-3 gap-3">
        {[0, 1, 2].map((i) => <div key={i} className="h-16 animate-pulse rounded-xl bg-slate-100 dark:bg-white/5" />)}
      </div>
    </div>
  );
}

export default function DevProjects() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  // The real message, not a boolean. Reporting every failure as "backend not
  // reachable" told people the server was down when it had answered in 40ms with a
  // 403 (role revoked), a 500, or a timeout — and sent them to support with it.
  const [error, setError] = useState(null);
  const [reloading, setReloading] = useState(false);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all"); // all | needs_attention | clean

  const load = useCallback(async ({ isRetry = false } = {}) => {
    if (isRetry) setReloading(true); else setLoading(true);
    setError(null);
    try {
      const res = await listDevProjects();
      setProjects(Array.isArray(res) ? res : []);
    } catch (e) {
      setError((e && e.message) || "Couldn't load your projects.");
    } finally {
      setLoading(false); setReloading(false);
    }
  }, []);

  useEffect(() => {
    let alive = true;
    // The callback owns the request; this only stops a late resolve writing to an
    // unmounted component.
    (async () => { if (alive) await load(); })();
    return () => { alive = false; };
  }, [load]);

  const totals = useMemo(() => projects.reduce((a, p) => ({
    cases: a.cases + (p.test_cases || 0),
    batches: a.batches + (p.batches || 0),
    issues: a.issues + (p.open_issues || 0),
  }), { cases: 0, batches: 0, issues: 0 }), [projects]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return projects.filter((p) => {
      if (filter === "needs_attention" && !(p.open_issues > 0)) return false;
      if (filter === "clean" && p.open_issues > 0) return false;
      if (!q) return true;
      return String(p.name || "").toLowerCase().includes(q)
        || String(p.base_url || "").toLowerCase().includes(q);
    });
  }, [projects, query, filter]);

  return (
    <>
      {/* hero */}
      <motion.div
        initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="relative mb-6 overflow-hidden rounded-2xl border border-slate-200 bg-white p-6 shadow-soft dark:border-white/10 dark:bg-ink-900 sm:p-7"
      >
        <Aurora className="opacity-60" />
        <div className="relative flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 text-white shadow-glow">
              <FolderKanban size={20} />
            </span>
            <div>
              <p className="text-[0.6875rem] font-semibold uppercase tracking-wider text-emerald-600 dark:text-emerald-300">Assigned to you</p>
              <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 dark:text-white sm:text-3xl">Projects</h1>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                Review the test reports, fix what&apos;s yours, and mark it Passed — the tester&apos;s
                next re-run verifies only what you changed.
              </p>
            </div>
          </div>
          {!loading && projects.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {[
                { icon: FileText, label: "cases", value: totals.cases, tone: "text-sky-500" },
                { icon: Boxes, label: "runs", value: totals.batches, tone: "text-brand-500" },
                { icon: Bug, label: "to fix", value: totals.issues, tone: "text-rose-500" },
              ].map((s) => (
                <span key={s.label} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200/70 bg-white/70 px-3 py-1.5 text-xs backdrop-blur dark:border-white/10 dark:bg-white/5">
                  <s.icon size={13} className={s.tone} />
                  <span className="font-semibold text-slate-900 dark:text-white">{s.value}</span>
                  <span className="text-slate-500 dark:text-slate-400">{s.label}</span>
                </span>
              ))}
            </div>
          )}
        </div>
      </motion.div>

      {/* search + filter */}
      {!loading && projects.length > 0 && (
        <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="relative w-full lg:max-w-md">
            <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={query} onChange={(e) => setQuery(e.target.value)}
              aria-label="Search projects by name or URL"
              placeholder="Search projects by name or URL…"
              className="w-full rounded-xl border border-slate-300 bg-white py-2.5 pl-9 pr-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-brand-500 focus:outline-none focus:ring-4 focus:ring-brand-500/15 dark:border-white/10 dark:bg-white/5 dark:text-white"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="hidden items-center gap-1 text-xs text-slate-400 sm:inline-flex"><Filter size={13} /> Filter</span>
            {[
              { key: "all", label: "All" },
              { key: "needs_attention", label: "Needs attention" },
              { key: "clean", label: "All clear" },
            ].map((o) => (
              <button key={o.key} type="button" onClick={() => setFilter(o.key)}
                // Active state was colour-only, so a screen reader announced three
                // identical buttons with no way to tell which filter was applied.
                aria-pressed={filter === o.key}
                className={cn("rounded-full px-3 py-1.5 text-xs font-medium transition-colors",
                  filter === o.key
                    ? "bg-brand-600 text-white shadow-soft"
                    : "border border-slate-200 text-slate-500 hover:bg-slate-100 dark:border-white/10 dark:text-slate-400 dark:hover:bg-white/5")}>
                {o.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          {[0, 1, 2].map((i) => <CardSkeleton key={i} />)}
        </div>
      ) : error ? (
        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-2.5 text-sm text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200">
          <WifiOff size={15} className="shrink-0" />
          <span className="flex-1">{error}</span>
          <button type="button" onClick={() => load({ isRetry: true })} disabled={reloading}
            className="inline-flex items-center gap-1 rounded-lg border border-amber-300 px-2.5 py-1 text-xs font-medium hover:bg-amber-100 disabled:opacity-60 dark:border-amber-500/30 dark:hover:bg-amber-500/20">
            {reloading
              ? <Loader2 size={12} className="animate-spin" />
              : <RotateCw size={12} />} Retry
          </button>
        </div>
      ) : projects.length === 0 ? (
        <EmptyState
          icon={FolderKanban}
          title="No projects assigned yet"
          message="When a tester assigns you to a project, it shows up here along with its test report."
        />
      ) : visible.length === 0 ? (
        <div className="card flex flex-col items-center gap-2 p-10 text-center">
          <Search className="text-slate-300" size={34} />
          <p className="text-sm text-slate-500 dark:text-slate-400">No projects match your search or filter.</p>
          <button onClick={() => { setQuery(""); setFilter("all"); }}
            className="text-sm font-medium text-brand-600 hover:text-brand-500 dark:text-brand-400">Clear filters</button>
        </div>
      ) : (
        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          {visible.map((p, i) => (
            <motion.button
              key={p.id} type="button"
              onClick={() => navigate(`/developer/projects/${p.id}`)}
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
              // Stagger only the first paint. Keying the delay off the filtered
              // index replayed the whole fade-in every time the result set changed,
              // so the grid flashed on each keystroke in the search box.
              transition={{ delay: query || filter !== "all" ? 0 : Math.min(i * 0.05, 0.4), duration: 0.4, ease: "easeOut" }}
              whileHover={{ y: -4 }}
              className="card group relative flex flex-col overflow-hidden p-5 text-left"
            >
              <div className="pointer-events-none absolute -right-10 -top-10 h-28 w-28 rounded-full bg-emerald-500/10 opacity-0 blur-2xl transition-opacity duration-300 group-hover:opacity-100" />
              <div className="relative flex items-start justify-between gap-2">
                <div className="flex min-w-0 items-center gap-2.5">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-emerald-500/15 to-teal-500/15 text-emerald-600 transition-transform duration-200 group-hover:scale-105 dark:text-emerald-300">
                    <FolderKanban size={20} />
                  </span>
                  {/* Truncated text needs the full value on hover, or two projects
                      whose names differ past the cut are indistinguishable. */}
                  <h3 title={p.name} className="truncate font-semibold text-slate-900 transition-colors group-hover:text-emerald-600 dark:text-white dark:group-hover:text-emerald-300">{p.name}</h3>
                </div>
                {/* "clear" only once a run has actually finished. A queued or
                    running batch reports zero failures, so a suite that had not
                    executed a single case wore the same green badge as a green one. */}
                {p.open_issues > 0 ? (
                  <span className="shrink-0 rounded-full bg-rose-50 px-2.5 py-0.5 text-xs font-semibold text-rose-600 dark:bg-rose-500/10 dark:text-rose-400">
                    {p.open_issues} to fix
                  </span>
                ) : p.last_batch?.in_progress ? (
                  <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-500 dark:bg-white/10 dark:text-slate-300">
                    <Loader2 size={11} className="animate-spin" /> running
                  </span>
                ) : (
                  <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-400">
                    <CheckCircle2 size={11} /> clear
                  </span>
                )}
              </div>

              <p className="relative mt-3 flex items-center gap-1.5 truncate text-sm text-slate-500 dark:text-slate-400">
                <Globe size={14} className="shrink-0" /> <span className="truncate" title={p.base_url}>{p.base_url}</span>
              </p>

              <div className="relative mt-4 grid grid-cols-2 gap-3">
                <div className="rounded-xl bg-slate-50 px-3 py-2.5 dark:bg-white/5">
                  <p className="flex items-center gap-1.5 text-xs text-slate-400"><FileText size={12} /> Test cases</p>
                  <p className="mt-0.5 text-lg font-bold text-slate-900 dark:text-white">{p.test_cases || 0}</p>
                </div>
                <div className="rounded-xl bg-slate-50 px-3 py-2.5 dark:bg-white/5">
                  <p className="flex items-center gap-1.5 text-xs text-slate-400"><Boxes size={12} /> Test runs</p>
                  <p className="mt-0.5 text-lg font-bold text-slate-900 dark:text-white">{p.batches || 0}</p>
                </div>
              </div>

              {p.last_batch && (
                <p className="relative mt-2 truncate text-xs text-slate-400">
                  Last run {fmtWhen(p.last_batch.created_at)} ·{" "}
                  {/* "0/50 passed" is what BOTH a queued run and a totally failed
                      one printed. Say which this is. */}
                  {p.last_batch.in_progress
                    ? `${p.last_batch.settled ?? 0}/${p.last_batch.total} done`
                    : `${p.last_batch.passed}/${p.last_batch.total} passed`}
                  {p.last_batch.rerun_count > 0 ? ` · re-run ×${p.last_batch.rerun_count}` : ""}
                </p>
              )}

              <div className="relative mt-5 border-t border-slate-100 pt-4 dark:border-white/10">
                <span className="inline-flex items-center gap-1 text-sm font-medium text-emerald-600 group-hover:text-emerald-500 dark:text-emerald-400">
                  Open report <ArrowRight size={15} className="transition-transform group-hover:translate-x-0.5" />
                </span>
              </div>
            </motion.button>
          ))}
        </div>
      )}
    </>
  );
}
